// knowledgeBaseNotificationsSlice.ts
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { KnowledgeBaseNotificationsService } from './knowledgeBaseNotificationsApi';

export type KnowledgeBaseNotificationStatus = 'unread' | 'read';

export type KnowledgeBaseNotificationType =
    | 'kb_exception_created'
    | 'kb_exception_resolved'
    | 'kb_report_responses_complete'
    | 'kb_report_generated_no_issues'
    | 'kb_report_generated_with_issues';

export interface KnowledgeBaseNotification {
    _id: string;
    hotelSub: string;
    agent?: {
        id: string;
        name: string;
    };
    type: KnowledgeBaseNotificationType;
    title: string;
    message: string;
    exceptionId?: string;
    reportId?: string;
    targetFileName?: string;
    category?: string;
    status: KnowledgeBaseNotificationStatus;
    readAt?: string | null;
    createdAt?: string;
    updatedAt?: string;

    reportCsvUrl?: string;
    reportPdfUrl?: string;
}

export interface IKnowledgeBaseNotificationsState {
    unreadCount: number;
    unreadIsLoading: boolean;

    notifications: KnowledgeBaseNotification[];
    notificationsAreLoading: boolean;

    pageNumber: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;

    error?: string | null;
}

const initialState: IKnowledgeBaseNotificationsState = {
    unreadCount: 0,
    unreadIsLoading: false,

    notifications: [],
    notificationsAreLoading: false,

    pageNumber: 1,
    pageSize: 10,
    totalElements: 0,
    totalPages: 0,

    error: null,
};

// GET /knowledge-base/notifications/unread-count
// knowledgeBaseNotificationsSlice.ts

export const tryToFetchKnowledgeBaseUnreadCount = createAsyncThunk<
    { unreadCount: number },
    void
>('knowledgeBaseNotifications/tryToFetchKnowledgeBaseUnreadCount', async () => {
    const res = await KnowledgeBaseNotificationsService.tryToFetchUnreadCount();
    const raw = res.data as any;

    let unreadCount = 0;

    // Handle a few common shapes
    if (typeof raw === 'number') {
        unreadCount = raw;
    } else if (raw?.data?.unreadCount != null) {
        unreadCount = raw.data.unreadCount;
    } else if (raw?.unreadCount != null) {
        unreadCount = raw.unreadCount;
    } else if (raw?.data?.count != null) {
        unreadCount = raw.data.count;
    } else if (raw?.count != null) {
        unreadCount = raw.count;
    }

    return { unreadCount };
});

// GET /knowledge-base/notifications?page=&size=  (includes unreadCount)
export const tryToFetchKnowledgeBaseNotifications = createAsyncThunk<
    {
        notifications: KnowledgeBaseNotification[];
        unreadCount: number;
        pageNumber: number;
        pageSize: number;
        totalElements: number;
        totalPages: number;
    },
    { pageNumber: number; pageSize: number }
>(
    'knowledgeBaseNotifications/tryToFetchKnowledgeBaseNotifications',
    async ({ pageNumber, pageSize }, { rejectWithValue }) => {
        try {
            const res =
                await KnowledgeBaseNotificationsService.tryToFetchNotifications(
                    pageNumber,
                    pageSize,
                );

            const raw = res.data as any;
            // your API shape is already at top-level
            const data = raw.data || raw;

            const elements: KnowledgeBaseNotification[] =
                data.items || // ✅ your API
                data.elements ||
                data.notifications ||
                data.data ||
                [];

            // map unreadCount correctly
            const unreadCount = data.unreadCount ?? raw.unreadCount ?? 0;

            const totalElements = data.total ?? elements.length;
            const size = data.size ?? pageSize;
            const page = data.page ?? pageNumber;
            const totalPages = size > 0 ? Math.ceil(totalElements / size) : 1;

            return {
                notifications: elements,
                unreadCount,
                pageNumber: page,
                pageSize: size,
                totalElements,
                totalPages,
            };
        } catch (error) {
            const err = error as AxiosError<any>;
            return rejectWithValue(
                err?.response?.data?.message || 'Failed to fetch notifications',
            );
        }
    },
);

export const tryToMarkKnowledgeBaseNotificationAsRead = createAsyncThunk<
    KnowledgeBaseNotification,
    string
>(
    'knowledgeBaseNotifications/tryToMarkKnowledgeBaseNotificationAsRead',
    async (id, { rejectWithValue }) => {
        try {
            const res = await KnowledgeBaseNotificationsService.tryToMarkAsRead(
                id,
            );
            const raw = res.data as any;
            const data = raw.data || raw; // in case you wrap in { data: ... }
            return data as KnowledgeBaseNotification;
        } catch (error) {
            const err = error as AxiosError<any>;
            return rejectWithValue(
                err?.response?.data?.message ||
                    'Failed to mark notification as read',
            );
        }
    },
);

export const knowledgeBaseNotificationsSlice = createSlice({
    name: 'knowledgeBaseNotifications',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // unread count
            .addCase(tryToFetchKnowledgeBaseUnreadCount.pending, (state) => {
                state.unreadIsLoading = true;
            })
            .addCase(tryToFetchKnowledgeBaseUnreadCount.rejected, (state) => {
                state.unreadIsLoading = false;
            })
            .addCase(
                tryToFetchKnowledgeBaseUnreadCount.fulfilled,
                (state, action) => {
                    state.unreadIsLoading = false;
                    state.unreadCount = action.payload.unreadCount ?? 0;
                },
            )

            // notifications list
            .addCase(tryToFetchKnowledgeBaseNotifications.pending, (state) => {
                state.notificationsAreLoading = true;
                state.error = null;
            })
            .addCase(
                tryToFetchKnowledgeBaseNotifications.rejected,
                (state, action) => {
                    state.notificationsAreLoading = false;
                    state.error =
                        (action.payload as string) ||
                        'Failed to fetch notifications';
                },
            )
            .addCase(
                tryToFetchKnowledgeBaseNotifications.fulfilled,
                (state, action) => {
                    state.notificationsAreLoading = false;

                    const {
                        notifications,
                        unreadCount,
                        pageNumber,
                        pageSize,
                        totalElements,
                        totalPages,
                    } = action.payload;

                    // First page = replace, later pages = append
                    if (pageNumber === 1) {
                        state.notifications = notifications;
                    } else {
                        const existingIds = new Set(
                            state.notifications.map((n) => n._id),
                        );
                        const merged = notifications.filter(
                            (n) => !existingIds.has(n._id),
                        );
                        state.notifications = [
                            ...state.notifications,
                            ...merged,
                        ];
                    }

                    state.unreadCount = unreadCount;
                    state.pageNumber = pageNumber;
                    state.pageSize = pageSize;
                    state.totalElements = totalElements;
                    state.totalPages = totalPages;
                },
            )
            .addCase(
                tryToMarkKnowledgeBaseNotificationAsRead.fulfilled,
                (state, action) => {
                    const updated = action.payload;
                    const idx = state.notifications.findIndex(
                        (n) => n._id === updated._id,
                    );

                    if (idx >= 0) {
                        const wasUnread =
                            state.notifications[idx].status === 'unread';
                        state.notifications[idx] = updated;

                        if (wasUnread && updated.status === 'read') {
                            state.unreadCount = Math.max(
                                0,
                                state.unreadCount - 1,
                            );
                        }
                    }
                },
            );
    },
});

export default knowledgeBaseNotificationsSlice.reducer;
